import React, { useState, useEffect } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaArrowLeft, FaChartPie, FaServer, FaBuilding, FaUsers, FaDatabase,
  FaClipboardCheck, FaExclamationTriangle, FaCheckCircle, FaTimesCircle,
  FaUserCircle, FaSignOutAlt, FaBell, FaSyncAlt, FaChartLine, FaBars
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const SaaSMetrics = () => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') { toast.error('Access denied'); navigate('/login'); return; }
    loadProfile(); loadMetrics();
  }, [navigate]);

  const loadProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { const d = await res.json(); setUserName(`${d.firstName || d.first_name || ''} ${d.lastName || d.last_name || ''}`); }
    } catch (e) {}
  };

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiEndpoint('/api/cloud-staff/metrics'), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) setMetrics(await res.json());
    } catch (e) { toast.error('Failed to load metrics'); }
    finally { setLoading(false); }
  };

  const handleLogout = () => { clearToken(); localStorage.removeItem('userRole'); localStorage.removeItem('token'); navigate('/login'); };

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge"><FaServer /><span>Cloud Staff Portal</span></div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <button className="cs-icon-btn"><FaBell /></button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header"><FaUserCircle className="cs-menu-avatar" /><div><p className="cs-menu-name">{userName}</p><p className="cs-menu-role">Cloud Staff Admin</p></div></div>
                  <div className="cs-user-menu-divider" />
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout"><FaSignOutAlt /> Sign Out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <div className="cs-layout">
        <CloudStaffSidebar active="metrics" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
        <main className="cs-content">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #21262d' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Link to="/cloud-staff/dashboard" className="act-back-btn"><FaArrowLeft /></Link>
              <div>
                <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f0f6fc', margin: 0 }}><FaChartPie style={{ marginRight: '0.5rem', color: '#ffa657' }} /> SaaS Metrics</h1>
                <p style={{ fontSize: '0.8rem', color: '#8b949e', margin: '0.2rem 0 0' }}>Business intelligence and usage analytics</p>
              </div>
            </div>
            <button onClick={loadMetrics} className="cs-primary-btn" disabled={loading}><FaSyncAlt /> Refresh</button>
          </div>

          {loading ? (
            <div className="cs-loading"><div className="cs-spinner" /><p>Loading metrics...</p></div>
          ) : metrics ? (
            <>
              {/* Top-level KPIs */}
              <div className="cs-stats-grid" style={{ marginBottom: '2rem' }}>
                <div className="cs-stat-card">
                  <div className="cs-stat-icon buildings"><FaBuilding /></div>
                  <div className="cs-stat-info">
                    <span className="cs-stat-value">{metrics.buildings?.total || 0}</span>
                    <span className="cs-stat-label">Total Buildings ({metrics.buildings?.active || 0} active)</span>
                  </div>
                </div>
                <div className="cs-stat-card">
                  <div className="cs-stat-icon today"><FaUsers /></div>
                  <div className="cs-stat-info">
                    <span className="cs-stat-value">{metrics.users?.total || 0}</span>
                    <span className="cs-stat-label">Total Users ({metrics.users?.active_30d || 0} active 30d)</span>
                  </div>
                </div>
                <div className="cs-stat-card">
                  <div className="cs-stat-icon total"><FaDatabase /></div>
                  <div className="cs-stat-info">
                    <span className="cs-stat-value">{metrics.assets?.total || 0}</span>
                    <span className="cs-stat-label">Total Assets</span>
                  </div>
                </div>
                <div className="cs-stat-card">
                  <div className="cs-stat-icon properties"><FaExclamationTriangle /></div>
                  <div className="cs-stat-info">
                    <span className="cs-stat-value" style={{ color: metrics.overdue_assets > 0 ? '#f85149' : '#3fb950' }}>{metrics.overdue_assets || 0}</span>
                    <span className="cs-stat-label">Overdue Assets</span>
                  </div>
                </div>
              </div>

              {/* Tests + Missing Refs */}
              <div className="cs-stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '2rem' }}>
                <div className="cs-stat-card">
                  <div className="cs-stat-info" style={{ width: '100%', textAlign: 'center' }}>
                    <span className="cs-stat-value" style={{ color: '#3fb950' }}>{metrics.tests_this_week || 0}</span>
                    <span className="cs-stat-label">Tests This Week</span>
                  </div>
                </div>
                <div className="cs-stat-card">
                  <div className="cs-stat-info" style={{ width: '100%', textAlign: 'center' }}>
                    <span className="cs-stat-value" style={{ color: '#f85149' }}>{metrics.failed_tests_this_week || 0}</span>
                    <span className="cs-stat-label">Failed Tests This Week</span>
                  </div>
                </div>
                <div className="cs-stat-card">
                  <div className="cs-stat-info" style={{ width: '100%', textAlign: 'center' }}>
                    <span className="cs-stat-value" style={{ color: '#ffa657' }}>{metrics.assets_missing_reference || 0}</span>
                    <span className="cs-stat-label">Assets Missing Reference</span>
                  </div>
                </div>
              </div>

              {/* Users by Role */}
              {metrics.users?.by_role && (
                <div className="cs-section">
                  <h2 className="cs-section-title">Users by Role</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem' }}>
                    {Object.entries(metrics.users.by_role).map(([role, count]) => (
                      <div key={role} style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '10px', padding: '1rem', textAlign: 'center' }}>
                        <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f0f6fc', display: 'block' }}>{count}</span>
                        <span style={{ fontSize: '0.75rem', color: '#8b949e', textTransform: 'capitalize' }}>{role || 'unknown'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Assets by Type */}
              {metrics.assets?.by_type && (
                <div className="cs-section">
                  <h2 className="cs-section-title">Assets by Type</h2>
                  <div className="cs-activity-table">
                    <table>
                      <thead><tr><th>Asset Type</th><th>Count</th><th>Distribution</th></tr></thead>
                      <tbody>
                        {Object.entries(metrics.assets.by_type).map(([key, at]) => (
                          <tr key={key}>
                            <td style={{ fontWeight: 500 }}>{at.label}</td>
                            <td style={{ fontWeight: 600 }}>{at.count}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{ flex: 1, height: '8px', background: '#21262d', borderRadius: '4px', overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${metrics.assets.total > 0 ? (at.count / metrics.assets.total * 100) : 0}%`, background: 'linear-gradient(90deg, #238636, #3fb950)', borderRadius: '4px', transition: 'width 0.3s' }} />
                                </div>
                                <span style={{ fontSize: '0.7rem', color: '#8b949e', minWidth: '35px', textAlign: 'right' }}>
                                  {metrics.assets.total > 0 ? Math.round(at.count / metrics.assets.total * 100) : 0}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tool Usage */}
              {metrics.tool_usage_30d && metrics.tool_usage_30d.length > 0 && (
                <div className="cs-section">
                  <h2 className="cs-section-title"><FaChartLine style={{ marginRight: '0.5rem' }} /> Tool Usage (Last 30 Days)</h2>
                  <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '12px', padding: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '120px' }}>
                      {metrics.tool_usage_30d.map((day, i) => {
                        const maxActions = Math.max(...metrics.tool_usage_30d.map(d => d.actions));
                        const height = maxActions > 0 ? (day.actions / maxActions * 100) : 0;
                        return (
                          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }} title={`${day.date}: ${day.actions} actions`}>
                            <div style={{ width: '100%', height: `${height}%`, minHeight: '2px', background: 'linear-gradient(180deg, #238636, #1a7f37)', borderRadius: '2px 2px 0 0', transition: 'height 0.3s' }} />
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                      <span style={{ fontSize: '0.65rem', color: '#484f58' }}>{metrics.tool_usage_30d[0]?.date}</span>
                      <span style={{ fontSize: '0.65rem', color: '#484f58' }}>{metrics.tool_usage_30d[metrics.tool_usage_30d.length - 1]?.date}</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="cs-empty-state"><FaTimesCircle /><p>Could not load metrics data</p></div>
          )}
        </main>
      </div>
    </div>
  );
};

export default SaaSMetrics;
